import { Button } from '@heroui/react'
import { MapPin, ExternalLink, Heart, ChevronRight, X, Star, Edit3, Copy } from 'lucide-react'
import { useTranslations } from 'use-intl'
import { LocalizedLink } from '~/components/common/localized-link'
import { toast } from 'sonner'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteNearbyShops } from '~/features/discovery/hooks/use-nearby-shops'
import { getRouteApi } from '@tanstack/react-router'
import { useFavorites } from '~/features/favorites/hooks/use-favorites'
import { FavoriteButton } from '~/features/favorites/components/FavoriteButton'
import { PrivateNoteModal } from '~/features/favorites/components/PrivateNoteModal'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '~/lib/api-client'
import { createSessionQuery } from '~/lib/session-query'
import { useAuthModalStore } from '~/stores/use-auth-modal-store'
import { getCleanShopName } from '~/utils/shop-name'

import { parseLocation } from '../../lib/geo-utils'

const routeApi = getRouteApi('/{-$locale}/discovery')

export function ShopQuickView({ shopId, locale, onClose }: { shopId: number, locale: string, onClose: () => void }) {
  const t = useTranslations('discovery')
  const navigate = routeApi.useNavigate()
  
  const search = routeApi.useSearch()
  
  // Read from the same infinite query cache that DiscoveryMap uses,
  // so clicking a map pin always finds the shop in cache.
  const { data: nearbyShopsData } = useInfiniteNearbyShops(locale, {
    lat: search.lat,
    lng: search.lng,
    radius: search.radius,
    categoryId: search.categoryId,
    query: search.q,
    budget: search.budget,
    nonSmoking: search.nonSmoking,
    hasPrivateRoom: search.hasPrivateRoom,
    kidsAllowed: search.kidsAllowed,
    favoritesOnly: search.favoritesOnly,
    snapshotId: search.snapshotId,
  })
  
  const shops = useMemo(() => {
    if (!nearbyShopsData) return []
    return nearbyShopsData.pages.flatMap((page: any) => page?.data || [])
  }, [nearbyShopsData])
  const cachedShop = shops.find((s: any) => s.id === shopId)

  const { data: fetchedShop, isLoading: isFetchingShop } = useQuery({
    queryKey: ['public', 'shops', shopId, locale],
    queryFn: async () => {
      // Fetch full details with translation since the list endpoint now trims heavy fields
      const res = await apiClient.shops['with-translation'][':id'][':locale'].$get({
        param: { id: String(shopId), locale },
      })
      if (!res.ok) throw new Error('Shop not found')
      const json = await res.json()
      return (json as any).data || json
    },
    enabled: !!shopId,
    initialData: cachedShop,
    initialDataUpdatedAt: 0, // Force background refetch to get the full translation fields
  })

  const shop = fetchedShop || cachedShop

  useEffect(() => {
    // Shared Intent Resolution: Auto-pan map if we had to fetch the shop
    if (!cachedShop && fetchedShop?.location) {
      const parsed = parseLocation(fetchedShop.location)
      if (parsed) {
        navigate({ search: (prev: any) => ({ ...prev, lat: parsed.lat, lng: parsed.lng }) })
      }
    }
  }, [cachedShop, fetchedShop])

  const { data: favorites } = useFavorites()
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)

  // Auth Guards
  const { data: session } = useQuery(createSessionQuery())
  const openAuthModal = useAuthModalStore(s => s.openAuthModal)

  useEffect(() => {
    if (!shop) return
    closeButtonRef.current?.focus({ preventScroll: true })
  }, [shop?.id])

  if (!shop && !isFetchingShop) return null
  if (!shop) return (
    <div className="fixed inset-y-0 right-0 z-50 w-full md:w-[400px] bg-white dark:bg-stone-900 shadow-2xl flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  )

  const favoriteRecord = Array.isArray(favorites) ? favorites.find((f: any) => f.shopId === shopId) : null
  const isFavorited = !!favoriteRecord

  const handleCopyAddress = async () => {
    // Attempt to grab address from translation object (from Drizzle join), or fallback
    const address = shop.address || shop.translation?.address || ''
    await navigator.clipboard.writeText(address)
    toast.success(t('quickView.addressCopied', { defaultValue: 'Address copied' }))
  }

  const handleTabelog = () => {
    toast.info(t('quickView.tabelogWarning', { defaultValue: 'Opening Tabelog...' }))
    // Using the real Tabelog URL from translation
    window.open(shop.translation?.url || '#', '_blank')
  }

  return (
    <>
      <div
        data-testid="shop-quick-view"
        role="dialog"
        aria-label={t('quickView.title', { defaultValue: 'Restaurant details' })}
        className="fixed inset-y-0 right-0 z-50 w-full md:w-[400px] bg-white dark:bg-stone-900 shadow-2xl flex flex-col transform transition-transform duration-300 translate-x-0 animate-in slide-in-from-right"
      >
        {/* Header / Close button */}
        <div className="absolute top-4 right-4 z-10">
          <Button
            isIconOnly
            aria-label={t('quickView.close', { defaultValue: 'Close quick view' })}
            variant="ghost"
            onPress={onClose}
            className="rounded-full bg-black/50 text-white backdrop-blur-md hover:bg-black/70"
            data-testid="shop-quick-view-close"
            ref={closeButtonRef}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto pb-24">
          {/* Hero Carousel */}
          <div className="relative group">
            <div className="w-full h-64 flex overflow-x-auto snap-x snap-mandatory no-scrollbar bg-stone-100 dark:bg-stone-800">
              {(shop.photos && shop.photos.length > 0 ? shop.photos.slice(0, 5) : [shop.imageUrl]).map((photo: string, i: number) => (
                <img key={i} src={photo} alt={`${shop.name} photo ${i + 1}`} className="w-full h-full object-cover shrink-0 snap-center" />
              ))}
            </div>
            {/* Carousel Dots */}
            {shop.photos && shop.photos.length > 1 && (
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
                {shop.photos.slice(0, 5).map((_: any, i: number) => (
                  <div key={i} className="w-1.5 h-1.5 rounded-full bg-white/50 backdrop-blur-sm shadow-sm" />
                ))}
              </div>
            )}
          </div>

          <div className="p-6">
            {/* Key Info */}
            <h2 className="text-2xl font-bold text-stone-900 dark:text-stone-100 mb-1 font-['Inter'] leading-tight">
              {getCleanShopName(shop.translation?.name || shop.name, shop.translation?.alias || shop.alias)}
            </h2>
            {shop.translation?.alias && (
              <p className="text-sm text-stone-500 dark:text-stone-400 mb-3">{shop.translation.alias}</p>
            )}
            
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded text-sm font-bold">
                <Star className="w-4 h-4 fill-current" />
                {shop.ratingScore || shop.rating}
              </div>
              {(shop.translation?.category || shop.category) && (
                <span className="text-xs text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-stone-800 px-2 py-1 rounded">
                  {shop.translation?.category || shop.category}
                </span>
              )}
              {shop.reviewCount && (
                <span className="text-xs text-stone-500 dark:text-stone-400">
                  {shop.reviewCount} {t('quickView.reviews', { defaultValue: 'reviews' })}
                </span>
              )}
            </div>

            {/* Action Row */}
            <div className="flex flex-col gap-2 mb-6">
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onPress={handleTabelog} className="flex-1 bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 font-medium">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {t('quickView.tabelog', { defaultValue: 'Tabelog' })}
                </Button>
                <FavoriteButton shopId={shop.id} showText className="flex-1" />
                <Button size="sm" variant="ghost" onPress={() => {
                  if (!session?.user) {
                    openAuthModal()
                    return
                  }
                  setIsNoteModalOpen(true)
                }} className="flex-1 bg-stone-200 dark:bg-stone-700 text-stone-700 dark:text-stone-300 font-medium">
                  <Edit3 className="w-4 h-4 mr-2" />
                  {t('addNote', { defaultValue: 'Add Note' })}
                </Button>
              </div>
            </div>

            {/* Description */}
            {shop.translation?.description && (
              <div className="mb-6">
                <h3 className="text-xs font-bold text-stone-500 dark:text-stone-400 mb-2 uppercase tracking-wider">{t('quickView.description', { defaultValue: 'Description' })}</h3>
                <p className="text-sm text-stone-700 dark:text-stone-300 leading-relaxed font-['Noto_Sans_JP'] line-clamp-6 hover:line-clamp-none transition-all">
                  {shop.translation.description}
                </p>
              </div>
            )}

            {/* Address */}
            {(shop.translation?.address || shop.address) && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <h3 className="text-xs font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider">{t('quickView.address', { defaultValue: 'Address' })}</h3>
                  <button onClick={handleCopyAddress} className="text-stone-400 hover:text-primary transition-colors" title={t('quickView.copyAddress', { defaultValue: 'Copy Address' })}>
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-stone-700 dark:text-stone-300 font-['Noto_Sans_JP']">{shop.translation?.address || shop.address}</p>
              </div>
            )}

            {(shop.lunchBudgetMin || shop.dinnerBudgetMin || shop.translation?.budget?.lunch || shop.translation?.budget?.dinner || shop.translation?.budget?.text) && (
              <div className="mb-4">
                <h3 className="text-xs font-bold text-stone-500 dark:text-stone-400 mb-1 uppercase tracking-wider">{t('quickView.budget', { defaultValue: 'Budget' })}</h3>
                <div className="flex flex-col gap-1 text-sm text-stone-700 dark:text-stone-300">
                  {/* Prioritize precise DB numerical fields if available */}
                  {(shop.lunchBudgetMin || shop.dinnerBudgetMin) ? (
                    <div className="flex flex-col gap-1">
                      {shop.lunchBudgetMin && (
                        <div><span className="text-xs text-stone-500 mr-1">{t('quickView.lunch', { defaultValue: 'Lunch' })}</span>¥{shop.lunchBudgetMin?.toLocaleString()}{shop.lunchBudgetMax ? `〜¥${shop.lunchBudgetMax?.toLocaleString()}` : ''}</div>
                      )}
                      {shop.dinnerBudgetMin && (
                        <div><span className="text-xs text-stone-500 mr-1">{t('quickView.dinner', { defaultValue: 'Dinner' })}</span>¥{shop.dinnerBudgetMin?.toLocaleString()}{shop.dinnerBudgetMax ? `〜¥${shop.dinnerBudgetMax?.toLocaleString()}` : ''}</div>
                      )}
                    </div>
                  ) : (
                    /* Fallback to translation strings */
                    <div className="flex flex-col gap-1">
                      {shop.translation?.budget?.lunch && (
                        <div><span className="text-xs text-stone-500 mr-1">{t('quickView.lunch', { defaultValue: 'Lunch' })}</span>{shop.translation.budget.lunch}</div>
                      )}
                      {shop.translation?.budget?.dinner && (
                        <div><span className="text-xs text-stone-500 mr-1">{t('quickView.dinner', { defaultValue: 'Dinner' })}</span>{shop.translation.budget.dinner}</div>
                      )}
                      {shop.translation?.budget?.text && !shop.translation?.budget?.lunch && !shop.translation?.budget?.dinner && (
                        <div className="whitespace-pre-line leading-relaxed">{shop.translation.budget.text.replace(/([0-9])(JPY|￥|¥)/g, '$1\n$2')}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Business Hours */}
            {shop.translation?.openHours && (
              <div className="mb-4">
                <h3 className="text-xs font-bold text-stone-500 dark:text-stone-400 mb-1 uppercase tracking-wider">{t('quickView.businessHours', { defaultValue: 'Business Hours' })}</h3>
                <div className="text-sm text-stone-700 dark:text-stone-300 font-['Noto_Sans_JP'] space-y-0.5 whitespace-pre-line">
                  {shop.translation.openHours}
                </div>
              </div>
            )}

            {/* Capacity */}
            {shop.capacity != null && (
              <div className="mb-4">
                <h3 className="text-xs font-bold text-stone-500 dark:text-stone-400 mb-1 uppercase tracking-wider">{t('quickView.capacity', { defaultValue: 'Seats' })}</h3>
                <p className="text-sm text-stone-700 dark:text-stone-300">{t('quickView.seats', { n: shop.capacity, defaultValue: `${shop.capacity} seats` })}</p>
              </div>
            )}

            {/* Highlights */}
            {(shop.prCommentContent || shop.translation?.prCommentContent) && (
              <div className="mb-6">
                <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100 mb-2 uppercase tracking-wider">{t('quickView.highlights', { defaultValue: 'Highlights' })}</h3>
                <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed font-['Noto_Sans_JP']">
                  {(shop.translation?.prCommentContent || shop.prCommentContent || '').substring(0, 100)}...
                </p>
              </div>
            )}
            
            {/* Note Display */}
            {favoriteRecord?.note && (
              <div className="mb-6 p-4 bg-primary/5 rounded-xl border border-primary/10">
                <h3 className="text-xs font-bold text-primary mb-1 uppercase tracking-wider">{t('quickView.privateNote', { defaultValue: 'Private Note' })}</h3>
                <p className="text-sm text-stone-700 dark:text-stone-300">{favoriteRecord.note}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer sticky CTA */}
        <div className="absolute bottom-0 left-0 w-full p-4 bg-white dark:bg-stone-900 border-t border-stone-200 dark:border-stone-800">
          <LocalizedLink
            to={`/shops/$shopId`}
            params={{ shopId: String(shop.id) }}
            className="flex w-full justify-center items-center py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors"
          >
            {t('quickView.viewDetails', { defaultValue: 'View Details' })}
            <ChevronRight className="w-5 h-5 ml-1" />
          </LocalizedLink>
        </div>
      </div>
      
      <PrivateNoteModal 
        isOpen={isNoteModalOpen} 
        onClose={() => setIsNoteModalOpen(false)} 
        shopId={shopId} 
        initialNote={favoriteRecord?.note} 
        initialTags={favoriteRecord?.tags}
      />
    </>
  )
}
